import React, { useMemo, useState } from 'react';
import { Divider, Flex, Spin } from 'antd';
import { LoginOutlined, LoadingOutlined } from '@ant-design/icons';
import { Button, ButtonType, SearchInput } from '@bettercomp/ui-components';
import { useDispatch } from 'react-redux';
import { useTenants } from 'hooks/useTenants';
import { loadUserData } from 'redux/user';
import type { TypedDispatch } from 'redux/configureStore';
import { reportError } from 'utils/reporting';
import { useTenantActions } from 'hooks/useTenantActions';
import { TenancyBroadcastMessageType, createTenancyBroadcastChannel } from '../../Layout/SuperUserBar/constants';
import cn from './styles.module.scss';

type TenantOption = {
  label: string;
  value: string;
};

const RETURN_HOME_VALUE = 'returnHome';


export function TenantSelector() {
  const dispatch = useDispatch<TypedDispatch>();
  const { availableTenants, isLoading } = useTenants();
  const { switchTenant, returnToHomeTenant } = useTenantActions();
  const [search, setSearch] = useState('');
  const [switchingToTenantId, setSwitchingToTenantId] = useState<string | null>(null);

  const filteredTenants = useMemo(() => {
    if (!search) {
      return availableTenants
    }
    return availableTenants.filter(({ name }) => name.toLocaleLowerCase().indexOf(search.toLowerCase()) > -1);
  }, [availableTenants, search]);

  const tenantOptions = React.useMemo((): TenantOption[] => {
    const options: TenantOption[] = filteredTenants
      .filter((tenant) => !tenant.is_current)
      .map(tenant => ({
        label: `${tenant.name} ${tenant.is_home ? '(Home)' : ''}`,
        value: tenant.tid,
      }));

    const homeTenant = availableTenants.find(t => t.is_home);
    const currentTenantInfo = availableTenants.find(t => t.is_current);

    if (homeTenant && currentTenantInfo && !currentTenantInfo.is_home) {
      return [
        {
          label: '🏠 Return to Home',
          value: RETURN_HOME_VALUE,
        },
        ...options,
      ]
    }

    return options;
  }, [filteredTenants, availableTenants]);

  const handleTenantChange = async (value: string) => {
    setSwitchingToTenantId(value);

    try {
      if (value === RETURN_HOME_VALUE) {
        await returnToHomeTenant();
      } else {
        await switchTenant(value);
      }

      await dispatch(loadUserData({ login: true }));

      // Notify all tabs to reload
      const channel = createTenancyBroadcastChannel();
      channel.postMessage(TenancyBroadcastMessageType.TenancySwitched);
    } catch (error) {
      reportError(error);
    } finally {
      setSwitchingToTenantId(null);
    }
  };

  const isAnySwitching = switchingToTenantId !== null;

  return (
    <div className={cn.tenantSelectContainer} data-testid="tenantSelector">
      <div className={cn.searchInputWrapper}>
        <SearchInput allowClear size="medium" value={search} onChange={setSearch} disabled={isAnySwitching} />
      </div>
      <Divider className={cn.divider} />
      <div className={cn.tenantList} data-testid="tenantsList">
        {isLoading ? (
          <Flex justify="center" align="center" className={cn.spinContainer}>
            <Spin size="small" />
          </Flex>
        ) : tenantOptions.map((tenant) => {
          const isSwitchingToThisTenant = switchingToTenantId === tenant.value;

          return (
            <Flex
              justify="space-between"
              align="center"
              key={tenant.value}
              className={cn.tenantSelectorItem}
              data-testid={`tenantListItem_${tenant.label}`}
            >
              <div className={cn.tenantLabelWrapper}>
                <div className={cn.tenantLabel}>{tenant.label}</div>
              </div>
              <Button
                type={ButtonType.Icon}
                icon={isSwitchingToThisTenant ? <LoadingOutlined spin /> : <LoginOutlined />}
                onClick={(event) => {
                  event.stopPropagation();
                  handleTenantChange(tenant.value)
                }}
                className={cn.loginButton}
                disabled={isAnySwitching}
                loading={isSwitchingToThisTenant}
                data-testid="loginTenantButton"
              />
            </Flex>
          );
        })}
      </div>
    </div>
  );
}
